import React from 'react';
import { Box, Paper, Typography, Button, Chip, Alert, Grid } from '@mui/material';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';
import { evidenceStatusChipMeta } from './incidentDrawerDetailStep2Utils';

export type IncidentDrawerDetailStep2StatusView = 'full' | 'summary' | 'technical';

export function IncidentDrawerDetailStep2Status({
  scope,
  view = 'full',
}: {
  scope: IncidentDrawerDetailScope;
  view?: IncidentDrawerDetailStep2StatusView;
}) {
  const s = scope as Record<string, any>;
  const snap = s.effectiveSnapshotStatus;
  const clip = s.effectiveClipStatus;
  const meta = s.effectiveMetaStatus;
  const showSummary = view === 'full' || view === 'summary';
  const showTechnical = view === 'full' || view === 'technical';

  if (view === 'technical' && !s.groupedReviewMode) {
    return null;
  }

  return (
    <Box ref={view === 'technical' ? undefined : s.drawerStep2Ref} sx={view === 'technical' ? { mt: 0 } : s.stepSectionSx('step2')}>
      <Paper variant="outlined" sx={s.drawerCardSx}>
        {view === 'technical' ? (
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            Evidence delivery and proof timing
          </Typography>
        ) : null}
        {!s.groupedReviewMode && !s.observationDispositionMode ? (
          <Typography sx={s.cardLabelSx}>{s.journeyPhaseTitleForStep('step2')} phase</Typography>
        ) : null}
        {showSummary ? (
          <>
            {s.renderAuditStepHeading(
              'step2',
              'Step 2: Verify evidence',
              s.groupedReviewMode ? undefined : `Proof available: ${s.proofSummary}.`,
            )}
            {s.groupedReviewMode ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Proof available for this representative evidence sample: {s.proofSummary}.
              </Typography>
            ) : null}
          </>
        ) : null}
        {showSummary ? (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(() => {
                const h = evidenceStatusChipMeta('snap', snap);
                return <Chip label={h.label} size="small" color={h.color} variant="outlined" sx={{ fontWeight: 800 }} />;
              })()}
              {(() => {
                const h = evidenceStatusChipMeta('clip', clip);
                return <Chip label={h.label} size="small" color={h.color} variant="outlined" sx={{ fontWeight: 800 }} />;
              })()}
              {meta
                ? (() => {
                    const h = evidenceStatusChipMeta('meta', meta);
                    return <Chip label={h.label} size="small" color={h.color} variant="outlined" sx={{ fontWeight: 800 }} />;
                  })()
                : null}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.2 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={!s.selectedIncident?.incident_id || !!s.drawerBusy}
                onClick={s._refreshSelectedIncident}
              >
                {s.effectiveClipStatus === 'PENDING' ? 'Refresh clip status' : 'Refresh evidence'}
              </Button>
              {s.relatedIncidentCount > 1 && !s.observationDispositionMode ? (
                <Button size="small" variant="text" onClick={s.openRelatedIncidents}>
                  {s.seriesActionLabel}
                </Button>
              ) : null}
            </Box>
          </>
        ) : null}
        {showTechnical ? (
        <Box sx={{ mt: showSummary ? 1.5 : 0, pt: showSummary ? 1.35 : 0, borderTop: showSummary ? '1px solid' : 'none', borderColor: 'divider' }} data-testid="edge-delivery-health">
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {s.groupedReviewMode ? 'Representative evidence status' : 'Evidence delivery health'}
          </Typography>
          <Alert severity={s.edgeDeliveryState.severity} sx={{ mb: 1.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.25 }}>
              {s.edgeDeliveryState.title}
            </Typography>
            <Typography variant="body2">{s.edgeDeliveryState.detail}</Typography>
          </Alert>
          {s.safeText(s.selectedIncident?.device_id) ? (
            <>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Cloud create lag</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {s.incidentCreateLagMs !== null ? s.formatAgeSeconds(Math.max(0, s.incidentCreateLagMs / 1000)) : '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Delay between edge event start and cloud incident creation.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Incident jobs active</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {Math.max(0, Number(s.incidentOutboxStats?.active_total || 0)).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Oldest queued incident: {s.formatAgeSeconds(Number(s.incidentOutboxStats?.oldest_pending_age_sec || 0))}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Evidence jobs active</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {Math.max(0, Number(s.evidenceOutboxStats?.active_total || 0)).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Oldest queued evidence: {s.formatAgeSeconds(Number(s.evidenceOutboxStats?.oldest_pending_age_sec || 0))}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">This incident on edge</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {s.incidentOutboxQueuedCount > 0
                      ? `${s.incidentOutboxQueuedCount} active job${s.incidentOutboxQueuedCount === 1 ? '' : 's'}`
                      : 'No active jobs for this incident'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.incidentOutboxQueuedCount > 0
                      ? Object.entries(s.edgeIncidentOutbox?.counts_by_status || {})
                          .filter(([, count]) => Number(count || 0) > 0)
                          .map(([status, count]) => `${status}: ${Number(count).toLocaleString()}`)
                          .join(' • ')
                      : 'The device is no longer retrying this incident create path.'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Last incident sent from edge</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {s.formatTimingInstant(s.parseTimingMs(s.edgeOutboxSummary?.last_sent_at?.INCIDENT_CREATE))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.safeText(s.edgeOutboxSummary?.mode?.mode_warning)
                      ? `Outbox mode warning: ${s.safeText(s.edgeOutboxSummary?.mode?.mode_warning)}`
                      : 'Read from the edge outbox summary for this device.'}
                  </Typography>
                </Grid>
              </Grid>
              {s.incidentRetryWaitTop || s.evidenceRetryWaitTop ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {s.incidentRetryWaitTop
                    ? `Top incident retry wait: ${s.safeText(s.incidentRetryWaitTop?.last_error) || 'unknown'}`
                    : s.evidenceRetryWaitTop
                      ? `Top evidence retry wait: ${s.safeText(s.evidenceRetryWaitTop?.last_error) || 'unknown'}`
                      : ''}
                </Typography>
              ) : null}
            </>
          ) : null}
        {s.evidenceTimingAvailable ? (
          <Box sx={{ mt: 1.5, pt: 1.35, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Timing truth
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={s.showSeparateAlertMoment ? 3 : 4}>
                <Typography variant="caption" color="text.secondary">
                  {s.eventLeadMs !== null && Math.abs(s.eventLeadMs) > 5000 ? 'Condition first detected' : 'Event happened'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {s.formatTimingInstant(s.eventStartedTimeMs)}
                </Typography>
              </Grid>
              {s.showSeparateAlertMoment ? (
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Incident raised on edge
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {s.formatTimingInstant(s.alertTriggeredTimeMs)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.eventLeadMs !== null && Math.abs(s.eventLeadMs) > 5000
                      ? `Condition persisted for ${s.formatTimingDelta(s.eventLeadMs)} before the edge raised this incident.`
                      : 'This is when the edge decided the event was actionable.'}
                  </Typography>
                </Grid>
              ) : null}
              <Grid item xs={12} md={s.showSeparateAlertMoment ? 3 : 4}>
                <Typography variant="caption" color="text.secondary">
                  Proof capture requested
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {s.formatTimingInstant(s.evidenceAnchorTimeMs)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.showSeparateAlertMoment && s.captureLeadMs !== null && Math.abs(s.captureLeadMs) > 1500
                    ? `Proof capture was requested ${s.formatTimingDelta(s.captureLeadMs)} after the edge raised this incident.`
                    : 'Proof timing is anchored to this request moment.'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={s.showSeparateAlertMoment ? 3 : 4}>
                <Typography variant="caption" color="text.secondary">Snapshot frame time</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {s.formatTimingInstant(s.snapshotFrameTimeMs)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.snapshotDeltaMs !== null
                    ? `Delta from evidence target: ${s.formatTimingDelta(s.snapshotDeltaMs)}`
                    : 'No frame delta available yet.'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={12}>
                <Typography variant="caption" color="text.secondary">Clip window</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {s.clipTiming.startMs && s.clipTiming.endMs
                    ? `${s.formatTimingInstant(s.clipTiming.startMs)} to ${s.formatTimingInstant(s.clipTiming.endMs)}`
                    : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.clipTiming.covered === true
                    ? 'Evidence target falls inside this clip.'
                    : s.clipTiming.covered === false
                      ? `Evidence target falls outside this clip by ${s.formatTimingDelta(s.clipTiming.gapMs)}.`
                      : 'Clip alignment is not available yet.'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        ) : null}
        </Box>
        ) : null}
      </Paper>
      {view === 'full' ? s.renderStepNavigation('step2') : null}
    </Box>
  );
}
